<template>
  <Modal
    size="xl"
    @close="$emit('close')"
  >
    <template #header>
      {{ $t('HeadContract.VariationsSchedule.VariationShowModal.title') }}
    </template>

    <template
      v-if="fetchVariationOrderAction.is(States.COMPLETE) && variationOrder"
      #header-badge
    >
      <span
        :style="{ backgroundColor: statusColour, color: variationOrder.statusTextColour }"
        class="inline-flex items-center rounded-full text-xs font-medium px-2.5 py-1 shrink-0"
      >
        {{ statusTitle }}
      </span>
    </template>

    <template
      v-if="fetchVariationOrderAction.is(States.COMPLETE) && variationOrder && project"
      #header-actions
    >
      <template v-if="project?.unarchived">
        <button
          v-if="canRepriceVariation"
          type="button"
          class="btn btn--secondary-blue"
          @click="showRepriceModal = true"
        >
          {{ $t('HeadContract.VariationsSchedule.VariationShowModal.button.reprice') }}
        </button>

        <button
          v-if="canBackChargeVariation"
          type="button"
          class="btn btn--secondary-blue"
          @click="showBackChargeModal = true"
        >
          {{ $t('HeadContract.VariationsSchedule.VariationShowModal.button.backCharge') }}
        </button>

        <button
          v-if="variationOrder.status === VariationOrderStatus.approved"
          type="button"
          class="btn btn--secondary-blue"
          @click="completeVariationOrder"
        >
          {{ $t('HeadContract.VariationsSchedule.VariationShowModal.button.markAsComplete') }}
        </button>

        <button
          v-if="variationOrder.status < VariationOrderStatus.approved && variationOrder.status !== VariationOrderStatus.principled"
          type="button"
          class="btn btn--secondary-alert"
          @click="declineVariationOrder"
        >
          {{ $t('HeadContract.VariationsSchedule.VariationShowModal.button.markAsDeclined') }}
        </button>
      </template>

      <button
        type="button"
        class="btn btn--secondary-blue flex items-center gap-2"
        @click="showDetails = !showDetails"
      >
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" v-if="showDetails"
         
          class="w-3.5 h-3.5">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
</svg>

        <span>
          {{ showDetails ? $t('HeadContract.VariationsSchedule.VariationShowModal.button.viewAttachments') : $t('HeadContract.VariationsSchedule.VariationShowModal.button.viewDetails') }}
        </span>
      </button>
    </template>

    <template #body>
      <div class="h-full w-full">
        <div
          v-if="fetchVariationOrderAction.is(States.COMPLETE) && variationOrder && project"
          class="relative"
        >
          <div
            v-if="showDetails && variationOrder"
            class="grid grid-cols-1 lg:grid-cols-5 -m-6"
          >
            <!-- Left: editable form -->
            <div class="lg:col-span-3 px-6 py-5 space-y-5 lg:border-r border-slate-200">
              <div
                v-if="variationOrder.delay"
                class="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2"
              >
                <p class="text-sm font-semibold text-blue-900">
                  {{ $t('HeadContract.VariationsSchedule.VariationShowModal.attachedToDelay') }}
                  <span>{{ variationOrder.delay.code }}</span>
                </p>

                <p class="text-sm text-blue-800 mt-0.5">
                  {{ variationOrder.delay.description }}
                </p>
              </div>

              <div
                v-if="variationOrder.hasBackCharge"
                class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
              >
                <div class="flex items-start gap-2">
                  <RouterLink
                    v-if="canViewBackChargeTarget"
                    class="btn-icon btn-icon-blue shrink-0"
                    :to="{
                      name: 'PackageVariationsSchedule',
                      params: {
                        projectId: variationOrder.transferred_to_variation_order?.project_id,
                        packageId: variationOrder.transferred_to_variation_order?.package_id,
                        variationId: variationOrder.transferred_to_variation_order?.id
                      }
                    }"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
                  </RouterLink>

                  <div class="min-w-0 space-y-0.5">
                    <p class="text-sm font-semibold text-amber-900">
                      {{ $t('HeadContract.VariationsSchedule.VariationShowModal.backChargedTo') }}
                      <span v-if="variationOrder.transferred_to_variation_order">
                        {{ variationOrder.transferred_to_variation_order.code }}
                      </span>
                    </p>

                    <p
                      v-if="variationOrder.transferred_to_variation_order?.package"
                      class="text-sm text-amber-800"
                    >
                      {{ $t('HeadContract.VariationsSchedule.VariationShowModal.backChargedPackage') }}:
                      {{ variationOrder.transferred_to_variation_order.package.name }}
                    </p>

                    <p
                      v-if="variationOrder.transferred_to_variation_order?.package?.subcontractor"
                      class="text-sm text-amber-800"
                    >
                      {{ $t('HeadContract.VariationsSchedule.VariationShowModal.backChargedVendor') }}:
                      {{ variationOrder.transferred_to_variation_order.package.subcontractor.name }}
                    </p>

                    <p
                      v-if="backChargeTargetDate"
                      class="text-sm text-amber-800"
                    >
                      {{ $t('HeadContract.VariationsSchedule.VariationShowModal.backChargedDate') }}:
                      {{ backChargeTargetDate.toLocaleString() }}
                    </p>
                  </div>
                </div>
              </div>

              <div
                v-else-if="variationOrder.isBackCharge"
                class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
              >
                <div class="flex items-start gap-2">
                  <RouterLink
                    v-if="canViewBackChargeSource"
                    class="btn-icon btn-icon-blue shrink-0"
                    :to="{
                      name: 'PackageVariationsSchedule',
                      params: {
                        projectId: variationOrder.transferred_from_variation_order?.project_id,
                        packageId: variationOrder.transferred_from_variation_order?.package_id,
                        variationId: variationOrder.transferred_from_variation_order?.id
                      }
                    }"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
                  </RouterLink>

                  <div class="min-w-0 space-y-0.5">
                    <p class="text-sm font-semibold text-amber-900">
                      {{ $t('HeadContract.VariationsSchedule.VariationShowModal.backChargedFrom') }}
                      <span v-if="variationOrder.transferred_from_variation_order">
                        {{ variationOrder.transferred_from_variation_order.code }}
                      </span>
                    </p>

                    <p
                      v-if="variationOrder.transferred_from_variation_order?.package"
                      class="text-sm text-amber-800"
                    >
                      {{ $t('HeadContract.VariationsSchedule.VariationShowModal.backChargedPackage') }}:
                      {{ variationOrder.transferred_from_variation_order.package.name }}
                    </p>

                    <p
                      v-if="variationOrder.transferred_from_variation_order?.package?.subcontractor"
                      class="text-sm text-amber-800"
                    >
                      {{ $t('HeadContract.VariationsSchedule.VariationShowModal.backChargedVendor') }}:
                      {{ variationOrder.transferred_from_variation_order.package.subcontractor.name }}
                    </p>

                    <p
                      v-if="backChargeSourceDate"
                      class="text-sm text-amber-800"
                    >
                      {{ $t('HeadContract.VariationsSchedule.VariationShowModal.backChargedDate') }}:
                      {{ backChargeSourceDate.toLocaleString() }}
                    </p>
                  </div>
                </div>
              </div>

              <form class="space-y-5">
                <label class="block">
                  <span class="block text-sm font-medium text-slate-500 mb-1.5">
                    {{ $t('HeadContract.VariationsSchedule.VariationShowModal.label.description') }}
                    <span class="text-slate-700 font-semibold">{{ variationOrder.code }}</span>
                  </span>

                  <AppInput
                    v-model="variationOrderForm.form.description"
                    type="text"
                    name="description"
                    class="form-input"
                    :error="variationOrderForm.getErrors('description')"
                  />
                </label>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label class="block">
                    <span class="block text-sm font-medium text-slate-500 mb-1.5">
                      {{ project?.isContractor ? $t('HeadContract.VariationsSchedule.VariationShowModal.label.clientReference') : $t('HeadContract.VariationsSchedule.VariationShowModal.label.contractorReference') }}
                    </span>

                    <AppInput
                      v-model="variationOrderForm.form.client_ref"
                      type="text"
                      name="client_ref"
                      class="form-input"
                      :error="variationOrderForm.getErrors('client_ref')"
                    />
                  </label>

                  <div class="block">
                    <div class="flex items-center justify-between mb-1.5">
                      <span class="text-sm font-medium text-slate-500">
                        Category
                      </span>

                      <button
                        v-if="variationOrderForm.form.variation_order_category_id"
                        type="button"
                        class="text-xs text-red-600 hover:text-red-700 font-medium"
                        @click="variationOrderForm.form.variation_order_category_id = undefined"
                      >
                        Remove
                      </button>
                    </div>

                    <AppSelect
                      v-model.number="variationOrderForm.form.variation_order_category_id"
                      name="variation_order_category_id"
                      class="form-input"
                      optional
                      empty-text="Uncategorized"
                      :options="categoryOptions"
                      :error="variationOrderForm.getErrors('variation_order_category_id')"
                    />
                  </div>
                </div>

                <label
                  v-if="!variationOrder.date_of_occurrence"
                  class="block"
                >
                  <span class="block text-sm font-medium text-slate-500 mb-1.5">
                    {{ $t('HeadContract.VariationsSchedule.VariationShowModal.label.dateOfOccurrence') }}
                  </span>

                  <AppInput
                    v-model="variationOrderForm.form.date_of_occurrence"
                    :error="variationOrderForm.getErrors('date_of_occurrence')"
                    class="form-input"
                    type="date"
                    name="date_of_occurrence"
                  />
                </label>

                <label class="block">
                  <span class="block text-sm font-medium text-slate-500 mb-1.5">
                    {{ $t('HeadContract.VariationsSchedule.VariationShowModal.label.notes') }}
                  </span>

                  <textarea
                    v-model="variationOrderForm.form.notes"
                    rows="3"
                    class="resize-none form-input"
                  />

                  <p
                    v-if="variationOrderForm.hasErrors('notes')"
                    class="text-sm text-red-600 mt-1"
                  >
                    {{ variationOrderForm.getErrors('notes') }}
                  </p>
                </label>

                <div class="pt-4 border-t border-slate-100">
                  <div class="flex items-center justify-between">
                    <h3 class="text-sm font-semibold text-slate-900">
                      {{ $t('HeadContract.VariationsSchedule.VariationShowModal.headContractVariation.label') }}
                    </h3>

                    <button
                      v-if="variationOrderForm.form.head_contract_variation_id && project?.unarchived"
                      type="button"
                      class="text-sm text-red-600 hover:text-red-700 font-medium"
                      @click="variationOrderForm.form.head_contract_variation_id = undefined"
                    >
                      Remove
                    </button>
                  </div>

                  <p class="text-xs text-slate-500 mt-0.5 mb-2">
                    {{ $t('HeadContract.VariationsSchedule.VariationShowModal.headContractVariation.hint') }}
                  </p>

                  <div class="flex items-center gap-2">
                    <div class="flex-1 w-full">
                      <AppSelect
                        v-model="variationOrderForm.form.head_contract_variation_id"
                        :options="headContractVariationOptions.map((vo) => ({ value: vo.id, label: `${vo.code} - ${vo.description}` }))"
                        :disabled="!project?.unarchived"
                        name="head_contract_variation_id"
                        :error="variationOrderForm.getErrors('head_contract_variation_id')"
                        class="form-input flex-1"
                      />
                    </div>

                    <RouterLink
                      v-if="variationOrder.head_contract_variation_id && project?.id"
                      :to="{
                        name: 'VariationsSchedule', params: { projectId: project.id, variationId: variationOrder.head_contract_variation_id }
                      }"
                      class="btn-icon btn-icon-blue block"
                      title="View Variation Order"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
                    </RouterLink>
                  </div>
                </div>
              </form>
            </div>

            <!-- Right: read-only summary -->
            <div class="lg:col-span-2 bg-slate-50 px-6 py-5 space-y-5 border-t lg:border-t-0 border-slate-200">
              <div class="space-y-2">
                <div class="flex items-baseline justify-between gap-3">
                  <span class="text-sm font-medium text-slate-500">
                    {{ $t('HeadContract.VariationsSchedule.VariationShowModal.dateOccurred') }}
                  </span>

                  <span
                    v-if="variationOrder.dateOfOccurrence"
                    class="text-sm font-semibold text-slate-900 text-right"
                  >{{ variationOrder.dateOfOccurrence.toLocaleString() }}</span>

                  <span
                    v-else
                    class="text-sm font-semibold text-slate-900 text-right"
                  >{{ $t('HeadContract.VariationsSchedule.VariationShowModal.notSubmitted') }}</span>
                </div>

                <div class="flex items-baseline justify-between gap-3">
                  <span class="text-sm font-medium text-slate-500">
                    {{ $t('HeadContract.VariationsSchedule.VariationShowModal.raisedBy', { name: variationOrder.user?.name }) }}
                  </span>

                  <span class="text-sm font-semibold text-slate-900 text-right">{{ variationOrder.createdAt.toLocaleString() }}</span>
                </div>

                <div
                  v-if="variationOrder.registeredAt && project?.isContractor"
                  class="flex items-baseline justify-between gap-3"
                >
                  <span class="text-sm font-medium text-slate-500">
                    {{ $t('HeadContract.VariationsSchedule.VariationShowModal.accepted') }}
                  </span>

                  <span class="text-sm font-semibold text-slate-900 text-right">{{ variationOrder.registeredAt.toLocaleString() }}</span>
                </div>

                <div
                  v-if="variationOrder.requiresNotice && noticeDueDate && !variationOrder.notice_sent_at"
                  class="flex items-baseline justify-between gap-3 rounded px-1.5 py-1 -mx-1.5"
                  :class="noticeClass(noticeDueIn)"
                >
                  <span class="text-sm font-medium text-slate-500">
                    {{ $t('HeadContract.VariationsSchedule.VariationShowModal.dateToBeIssuedBy') }}
                  </span>

                  <span class="text-sm font-semibold text-slate-900 text-right">{{ noticeDueDate.toLocaleString() }}</span>
                </div>

                <div
                  v-if="variationOrder.noticeSentAt"
                  class="flex items-baseline justify-between gap-3"
                >
                  <span class="text-sm font-medium text-slate-500">
                    {{ project.isClient ? $t('HeadContract.VariationsSchedule.VariationShowModal.noticeRegistered') : $t('HeadContract.VariationsSchedule.VariationShowModal.noticeSent') }}
                  </span>

                  <span class="text-sm font-semibold text-slate-900 text-right">{{ variationOrder.noticeSentAt.toLocaleString() }}</span>
                </div>

                <div
                  v-if="variationOrder.submittedAt && project.isContractor"
                  class="flex items-baseline justify-between gap-3"
                >
                  <span class="text-sm font-medium text-slate-500">
                    {{ $t('HeadContract.VariationsSchedule.VariationShowModal.pricingSubmitted') }}
                  </span>

                  <span class="text-sm font-semibold text-slate-900 text-right">{{ variationOrder.submittedAt.toLocaleString() }}</span>
                </div>

                <div
                  v-if="variationOrder.provisionedAt"
                  class="flex items-baseline justify-between gap-3"
                >
                  <span class="text-sm font-medium text-slate-500">
                    {{ project.isClient ? $t('HeadContract.VariationsSchedule.VariationShowModal.pricingReceived') : $t('HeadContract.VariationsSchedule.VariationShowModal.pricingApproved') }}
                  </span>

                  <span class="text-sm font-semibold text-slate-900 text-right">{{ variationOrder.provisionedAt.toLocaleString() }}</span>
                </div>

                <div
                  v-if="variationOrder.approvedAt"
                  class="flex items-baseline justify-between gap-3"
                >
                  <span class="text-sm font-medium text-slate-500">
                    {{ $t('HeadContract.VariationsSchedule.VariationShowModal.fullyApproved') }}
                  </span>

                  <span class="text-sm font-semibold text-slate-900 text-right">{{ variationOrder.approvedAt.toLocaleString() }}</span>
                </div>
              </div>

              <div class="border-t border-slate-200" />

              <div class="space-y-3">
                <div class="flex items-baseline justify-between gap-3">
                  <span class="text-sm font-medium text-slate-500">
                    {{ $t('HeadContract.VariationsSchedule.VariationShowModal.label.claimed') }}
                  </span>

                  <div>
                    <div
                      v-if="variationOrder.totalBudget && !editingSumPrice"
                      class="group flex items-center justify-end gap-1"
                    >
                      <button
                        v-if="!project?.isContractor && canEditSumPrice"
                        type="button"
                        class="btn-icon group-hover:visible btn-icon-blue inline-block invisible"
                        @click="editSumPrice"
                      >
                        <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
                      </button>

                      <span class="text-sm font-semibold text-slate-900">
                        {{ $tcc(variationOrder.totalBudget) }}
                      </span>
                    </div>

                    <template v-else-if="project?.isContractor">
                      <div v-if="project.unarchived && !variationOrder.totalBudget && (variationOrder.status === VariationOrderStatus.registered || variationOrder.status === VariationOrderStatus.principled || variationOrder.status === VariationOrderStatus.priced)">
                        <AppCurrencyInput
                          v-model.number="sumPrice"
                          name="pricing"
                          class="form-input w-24 p-0 ml-auto"
                          @blur="priceVariationOrder"
                          @keyup.enter="priceVariationOrder"
                        />
                      </div>
                    </template>

                    <template v-else>
                      <div v-if="project.unarchived && (variationOrder.status === VariationOrderStatus.registered || variationOrder.status === VariationOrderStatus.principled || variationOrder.status === VariationOrderStatus.priced)">
                        <AppCurrencyInput
                          ref="sumPriceInput"
                          v-model.number="sumPrice"
                          name="pricing"
                          class="form-input w-24 p-0 ml-auto"
                          @blur="priceVariationOrder"
                          @keyup.enter="$event.target.blur()"
                          @keyup.esc="cancelEditSumPrice"
                        />
                      </div>
                    </template>
                  </div>
                </div>

                <template v-if="variationOrder.type === VariationOrderType.provisional_sum">
                  <div>
                    <div class="flex items-center justify-between mb-1.5">
                      <span class="text-sm font-medium text-slate-500">Linked package trade item</span>

                      <button
                        v-if="variationOrderForm.form.package_trade_item_id && !variationOrder.$hasCosts"
                        type="button"
                        class="text-xs text-red-600 hover:text-red-700 font-medium"
                        @click="variationOrderForm.form.package_trade_item_id = undefined"
                      >
                        Remove
                      </button>
                    </div>

                    <AppSelect
                      v-model.number="variationOrderForm.form.package_trade_item_id"
                      name="package_trade_item_id"
                      class="form-input bg-white"
                      :options="provisionalSumPackageTradeItemOptions"
                      :disabled="variationOrder.$hasCosts"
                      :optional="true"
                      empty-text="Select a provisional sum item"
                    />
                  </div>

                  <div
                    v-if="showContractItem && linkedTradeItemBudget !== undefined"
                    class="rounded-lg border border-slate-200 bg-white p-4"
                  >
                    <h4 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                      Item budget
                    </h4>

                    <div class="space-y-1.5 text-sm">
                      <div class="flex justify-between text-slate-600">
                        <span>Contract item budget</span>

                        <span>{{ $tcc(linkedTradeItemBudget) }}</span>
                      </div>

                      <div
                        v-if="variationOrder.$otherLinkedVariationsBudget"
                        class="flex justify-between text-slate-600"
                      >
                        <span>Other linked variations</span>

                        <span>{{ $tcc(variationOrder.$otherLinkedVariationsBudget) }}</span>
                      </div>

                      <div
                        v-if="variationOrder.totalBudget"
                        class="flex justify-between text-slate-600"
                      >
                        <span>This variation</span>

                        <span>{{ $tcc(variationOrder.totalBudget) }}</span>
                      </div>
                    </div>

                    <div class="flex justify-between items-baseline mt-3 pt-3 border-t border-slate-200">
                      <span class="text-sm font-semibold text-slate-900">Final item budget</span>

                      <span class="font-bold text-slate-900">
                        {{ $tcc(linkedTradeItemBudget + (variationOrder.$otherLinkedVariationsBudget || 0) + (variationOrder.totalBudget || 0)) }}
                      </span>
                    </div>
                  </div>
                </template>
              </div>

              <div
                v-if="variationOrder.trade_items?.length > 0"
                class="pt-1"
              >
                <h4 class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  {{ $t('HeadContract.VariationsSchedule.VariationShowModal.label.budgetBreakdown') }}
                </h4>

                <ul class="max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
                  <li
                    v-for="tradeItem in variationOrder.trade_items"
                    :key="tradeItem.id"
                    class="flex justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <span class="text-slate-600">{{ tradeItem.name }}</span>

                    <span class="font-medium text-slate-900">{{ $tcc(tradeItem.budget) }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div
            v-else
            class="relative"
          >
            <div
              v-if="variationOrderMedia.mediaCollection.value.length === 0"
              class="text-sm font-semibold text-slate-700 mb-4 text-center"
            >
              {{ $t('HeadContract.VariationsSchedule.VariationShowModal.noAttachmentsFound') }}
            </div>

            <FileUpload
              v-if="project?.unarchived"
              v-model="fileUploadForm"
              multiple
              @change="fileUploaded"
            />

            <button
              v-if="variationOrderMedia.mediaCollection.value.length > 1"
              class="absolute left-0 inset-y-1/2 border border-slate-300 rounded-full w-8 h-8 flex justify-center items-center hover:bg-slate-100 active:bg-slate-200 z-10 bg-white"
              type="button"
              @click="mediaSwiper.slidePrev()"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
  <path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clip-rule="evenodd" />
</svg>
            </button>

            <Swiper
              class="max-w-md mb-10"
              :space-between="20"
              :loop="variationOrderMedia.mediaCollection.value?.length > 1"
              centered-slides
              :modules="[Pagination]"
              :pagination="{
                el: '.swiper-pagination',
                type: 'bullets'
              }"
              @swiper="setSwiper"
            >
              <SwiperSlide
                v-for="media in variationOrderMedia.mediaCollection.value"
                :key="media.uuid"
                class="!h-auto"
              >
                <div class="flex items-center h-full w-full justify-center">
                  <MediaDisplay
                    class="max-w-60"
                    :media="media"
                    @delete="variationOrderMedia.removeMedia(media.uuid)"
                    @refresh="variationOrderMedia.refreshMedia(media.uuid)"
                  >
                    <p class="text-sm">
                      {{ $t('HeadContract.VariationsSchedule.VariationShowModal.attachToEmails') }}:
                    </p>

                    <div class="flex text-sm space-x-2">
                      <div
                        v-if="project?.isContractor"
                        class="flex space-x-1 items-center"
                      >
                        <p>
                          {{ $t('HeadContract.VariationsSchedule.VariationShowModal.label.notice') }}
                        </p>

                        <input
                          v-model="media.custom_properties.notice"
                          type="checkbox"
                          name="notice"
                          class="form-checkbox"
                          @change="variationOrderMedia.updateSettings(media.uuid, { notice: ($event.target as any).checked })"
                        >
                      </div>

                      <div
                        v-if="project?.isContractor"
                        class="flex space-x-1 items-center"
                      >
                        <p>
                          {{ $t('HeadContract.VariationsSchedule.VariationShowModal.label.submission') }}
                        </p>

                        <input
                          v-model="media.custom_properties.submission"
                          type="checkbox"
                          name="submission"
                          class="form-checkbox"
                          @change="variationOrderMedia.updateSettings(media.uuid, { submission: ($event.target as any).checked })"
                        >
                      </div>

                      <div class="flex space-x-1 items-center">
                        <p>
                          {{ $t('HeadContract.VariationsSchedule.VariationShowModal.label.approval') }}
                        </p>

                        <input
                          v-model="media.custom_properties.approval"
                          type="checkbox"
                          name="approval"
                          class="form-checkbox"
                          @change="variationOrderMedia.updateSettings(media.uuid, { approval: ($event.target as any).checked })"
                        >
                      </div>
                    </div>
                  </MediaDisplay>
                </div>
              </SwiperSlide>
            </Swiper>

            <div class="swiper-pagination" />

            <button
              v-if="variationOrderMedia.mediaCollection.value.length > 1"
              class="absolute right-0 inset-y-1/2 border border-slate-300 rounded-full w-8 h-8 flex justify-center items-center hover:bg-slate-100 active:bg-slate-200 z-10 bg-white"
              type="button"
              @click="mediaSwiper.slideNext()"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
            </button>
          </div>
        </div>

        <div v-else-if="fetchVariationOrderAction.is(States.LOADING)">
          <div class="flex justify-center">
            <AppSpinner />
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-between w-full">
        <button
          type="button"
          class="btn btn--tertiary-alert"
          @click="deleteVariationOrder"
        >
          {{ $t('HeadContract.VariationsSchedule.VariationShowModal.button.delete') }}
        </button>

        <div class="flex items-center gap-2">
          <button
            type="button"
            class="btn btn--secondary-blue"
            @click="$emit('close')"
          >
            {{ $t('HeadContract.VariationsSchedule.VariationShowModal.button.close') }}
          </button>

          <button
            v-if="project?.unarchived"
            type="button"
            class="btn--primary-blue btn"
            @click="updateVariationOrder"
          >
            {{ $t('HeadContract.VariationsSchedule.VariationShowModal.button.update') }}
          </button>
        </div>
      </div>
    </template>
  </Modal>

  <PackageVariationBackChargeModal
    v-if="showBackChargeModal && variationOrder"
    :variation-order="variationOrder"
    @close="showBackChargeModal = false"
    @back-charged="onBackCharged"
  />

  <PricingModal
    v-if="showRepriceModal && variationOrder"
    mode="reprice"
    :variation-order-id="variationOrder.id"
    @close="showRepriceModal = false"
    @priced="onRepriced"
  />
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import PricingModal from '@/components/Subcontract/PackageVariationsSchedule/PricingModal.vue';
import { useFormData } from '@/composables/useFormData';
import { Media, useMediaCollection } from '@/composables/useMediaCollection';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, dateForDateInput, enumKeyFromValue } from '@/helpers';
import {
  VariationOrderStatus,
  VariationOrderStatusColours,
  VariationOrderStatusTitles,
  VariationOrderType,
} from '@/models/VariationOrder';
import { usePackagesStore } from '@/store/packages';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { useVariationOrdersStore } from '@/store/variationOrders';
import Swal from 'sweetalert2';
import { Pagination } from 'swiper/modules';
import { Swiper as SwiperInterface } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import 'swiper/css/pagination';
import FileUpload from '@/components/FileUpload.vue';
import MediaDisplay from '@/components/MediaDisplay.vue';
import PackageVariationBackChargeModal from '@/components/Subcontract/PackageVariationsSchedule/PackageVariationBackChargeModal.vue';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { useVariationOrderCategoriesStore } from '@/store/variationOrderCategories';
import { RouterLink } from 'vue-router';

const props = defineProps<{
  variationOrderId: Id;
  startOnAttachments?: boolean;
}>();

const emits = defineEmits<{
  (e: 'close');
  (e: 'back-charged');
}>();

const variationOrdersStore = useVariationOrdersStore();
const packagesStore = usePackagesStore();
const packageTradeItemsStore = usePackageTradeItemsStore();
const userStore = useUserStore();
const fetchPackageAction = useStoreApiAction(packagesStore.show);
const fetchVariationOrderAction = useStoreApiAction(variationOrdersStore.fetchVariationOrder);
const fetchVariationOrdersIndexAction = useStoreApiAction(variationOrdersStore.fetchVariationOrders);
const fetchCorrespondencesAction = useStoreApiAction(variationOrdersStore.fetchCorrespondences);
const declineVariationOrderAction = useStoreApiAction(variationOrdersStore.declineVariationOrder);

watch(() => props.variationOrderId, (newValue, oldValue) => {
  if(newValue && newValue !== oldValue) {
    fetchVariationOrderAction.request(newValue).catch(console.error);
    fetchCorrespondencesAction.request(newValue).catch(console.error);
  }
}, { immediate: true });

const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.project;
});

const canRepriceVariation = computed(() => {
  return variationOrder.value?.status === VariationOrderStatus.approved &&
    userStore.isRole([
      'company_admin',
    ]);
});

const showRepriceModal = ref(false);

const onRepriced = () => {
  showRepriceModal.value = false;

  if(variationOrder.value?.id) {
    fetchVariationOrderAction.request(variationOrder.value.id).catch(console.error);
  }
};

watch(
  () => project.value?.id,
  (newValue, oldValue) => {
    if(newValue && newValue !== oldValue) {
      fetchVariationOrdersIndexAction.request(newValue, {}).catch(console.error);
    }
  },
  { immediate: true },
);

const variationOrder = computed(() => {
  return variationOrdersStore.models
    .with('trade_items')
    .with('user')
    .with('correspondences')
    .with('delay')
    .with('head_contract_variation')
    .with('package_trade_item', (query) => query.with('trade_item'))
    .with('transferred_to_variation_order', (query) =>
      query
        .with('project')
        .with('package', (packageQuery) => packageQuery.with('subcontractor')))
    .with('transferred_from_variation_order', (query) =>
      query
        .with('project')
        .with('package', (packageQuery) => packageQuery.with('subcontractor')))
    .where(
      'id',
      parseInt(props.variationOrderId as string),
    )
    .first();
});

const backChargeTargetDate = computed(() => {
  const related = variationOrder.value?.transferred_to_variation_order;

  return related?.registeredAt ?? related?.createdAt;
});

const backChargeSourceDate = computed(() => {
  return variationOrder.value?.registeredAt ?? variationOrder.value?.createdAt;
});

const showBackChargeModal = ref(false);

const canBackChargeVariation = computed(() => {
  return !!project.value?.unarchived &&
    !!variationOrder.value?.package_id &&
    !variationOrder.value?.isTransferred &&
    !variationOrder.value?.hasBackCharge &&
    !variationOrder.value?.isBackCharge &&
    (variationOrder.value?.totalBudget ?? 0) > 0;
});

const canViewBackChargeTarget = computed(() => {
  const related = variationOrder.value?.transferred_to_variation_order;

  return !!related?.project_id && !!related?.package_id;
});

const canViewBackChargeSource = computed(() => {
  const related = variationOrder.value?.transferred_from_variation_order;

  return !!related?.project_id && !!related?.package_id;
});

const onBackCharged = () => {
  fetchVariationOrderAction.request(props.variationOrderId).catch(console.error);
  emits('back-charged');
};

watch(() => variationOrder.value?.package_id, (packageId) => {
  if(packageId) {
    fetchPackageAction.request(packageId).catch(console.error);
  }
}, { immediate: true });

const provisionalSumPackageTradeItemOptions = computed(() => {
  if(!variationOrder.value?.package_id) {
    return [];
  }

  return packageTradeItemsStore.models
    .where('package_id', variationOrder.value.package_id)
    .with('trade_item')
    .get()
    .filter((packageTradeItem) => packageTradeItem.trade_item?.is_provisional_sum)
    .map((packageTradeItem) => ({
      value: packageTradeItem.id,
      label: `${packageTradeItem.trade_item?.name ?? 'Item'} (${australianCurrency(packageTradeItem.value)})`,
    }));
});

const linkedTradeItemBudget = computed(() => {
  return variationOrder.value?.package_trade_item?.value;
});

const headContractVariationOptions = computed(() => {
  if(!project.value?.id) {
    return [];
  }

  return variationOrdersStore.models
    .where('project_id', project.value?.id)
    .whereNull('package_id')
    .whereNotNull('code')
    .get();
});

const statusTitle = computed(() => {
  return VariationOrderStatusTitles[enumKeyFromValue(VariationOrderStatus, variationOrder.value.status)];
});

const statusColour = computed(() => {
  return VariationOrderStatusColours[enumKeyFromValue(VariationOrderStatus, variationOrder.value.status)];
});

const updateVariationOrderAction = useStoreApiAction(variationOrdersStore.updateVariationOrder);
const completeVariationOrderAction = useStoreApiAction(variationOrdersStore.completeVariationOrder);

const variationOrderForm = useFormData({
  client_ref: '',
  notes: '',
  description: '',
  date_of_occurrence: '',
  variation_order_category_id: undefined as number | undefined,
  head_contract_variation_id: undefined,
  package_trade_item_id: undefined as number | undefined,
});

watch(variationOrder, (newValue) => {
  if(newValue) {
    variationOrderForm.setData(newValue, [
      'date_of_occurrence',
      'head_contract_variation_id',
      'variation_order_category_id',
    ]);

    variationOrderForm.form.date_of_occurrence = dateForDateInput(newValue.date_of_occurrence ?? new Date());
    variationOrderForm.form.head_contract_variation_id = newValue.head_contract_variation_id ?? undefined;
    variationOrderForm.form.variation_order_category_id = newValue.variation_order_category_id ?? undefined;
    variationOrderForm.form.package_trade_item_id = newValue.package_trade_item_id ?? undefined;
  }
}, { immediate: true });

const categoriesStore = useVariationOrderCategoriesStore();

const categoryOptions = computed(() => {
  if(!variationOrder.value?.package_id) {
    return [];
  }

  return categoriesStore.models
    .where('project_id', variationOrder.value.project_id)
    .where('package_id', variationOrder.value.package_id)
    .orderBy('sort_order')
    .get()
    .map((category) => ({
      value: category.id,
      label: category.description,
    }));
});

watch(
  () => [variationOrder.value?.project_id, variationOrder.value?.package_id] as const,
  (newValue, oldValue) => {
    if(newValue[0] && newValue[1] && (!oldValue || oldValue[0] !== newValue[0] || oldValue[1] !== newValue[1])) {
      categoriesStore.indexPackage(newValue[0], newValue[1]).catch(console.error);
    }
  },
  { immediate: true },
);

const variationOrderMedia = useMediaCollection(variationOrdersStore.media);

watch(() => props.variationOrderId, (newValue) => {
  if(newValue) {
    variationOrderMedia.fetchMedia(newValue);
  }
}, { immediate: true });

const updateVariationOrder = () => {
  updateVariationOrderAction.request(props.variationOrderId, variationOrderForm.form)
    .then(() => {
      Swal.fire({
        icon: 'success',
        text: 'Variation Order Updated',
      });
    }).catch(console.error);
};

const declineVariationOrder = () => {
  Swal.fire({
    title: 'Mark variation order as declined?',
    icon: 'question',
    text: variationOrder.value.description,
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      declineVariationOrderAction.request(variationOrder.value.id).then(() => {
        Swal.fire({
          icon: 'success',
          text: 'Variation order marked as declined.',
        }).then(() => {
          emits('close');
        });
      }).catch((error) => {
        console.log(error);
      });
    }
  });
};

const completeVariationOrder = () => {
  Swal.fire({
    title: 'Mark Variation as Complete?',
    icon: 'question',
    text: 'This will prevent future draw downs against this variation.',
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      completeVariationOrderAction.request(variationOrder.value.id).then(() => {
        Swal.fire({
          icon: 'success',
          text: 'Variation Order Completed',
        }).then(() => {
          emits('close');
        });
      }).catch((error) => {
        console.log(error);
      });
    }
  });
};

const showDetails = ref(!props.startOnAttachments);
const mediaSwiper = ref<SwiperInterface>();

const setSwiper = (swiper: SwiperInterface) => {
  mediaSwiper.value = swiper;
};

const storeMediaAction = useStoreApiAction(variationOrdersStore.storeMedia);
const fileUploadForm = ref();

const fileUploaded = (event: Event) => {
  const files = Array.from((event.target as HTMLInputElement).files);
  const selectedFile = files[0];

  if(selectedFile) {
    const fileFormData = new FormData();

    fileFormData.append('images[]', selectedFile);

    storeMediaAction.request(variationOrder.value.id, fileFormData).then((medias) => {
      variationOrderMedia.addMedia(medias);
    }).catch((error) => {
      if(error?.status === 422) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to upload attachments',
          text: error.data.message,
        });
      }
    });
  }
};

const noticeDueIn = computed(() => {
  if(variationOrder.value.dateOfOccurrence) {
    return project.value.head_contract_settings.nov_period -
      Math.ceil(variationOrder.value.dateOfOccurrence.diffNow('days').days);
  }

  return project.value.head_contract_settings.nov_period;
});

const noticeClass = (noticeDueIn: number) => {
  if(noticeDueIn < 10 && noticeDueIn > 5) {
    return 'bg-orange-300';
  } else if(noticeDueIn < 5) {
    return 'bg-red-300';
  }

  return '';
};

const noticeDueDate = computed(() => {
  return variationOrder.value.dateOfOccurrence?.plus({ days: project.value.head_contract_settings.nov_period });
});

const canEditSumPrice = computed(() => {
  return project.value?.unarchived && (
    variationOrder.value?.status === VariationOrderStatus.registered ||
    variationOrder.value?.status === VariationOrderStatus.principled ||
    variationOrder.value?.status === VariationOrderStatus.priced
  );
});

const editingSumPrice = ref(false);
const sumPriceInput = useTemplateRef('sumPriceInput');

const editSumPrice = () => {
  editingSumPrice.value = true;
  sumPrice.value = variationOrder.value?.totalBudget;

  nextTick(() => {
    sumPriceInput.value?.focusInput();
  });
};

const cancelEditSumPrice = () => {
  editingSumPrice.value = false;
};

const sumPrice = ref(variationOrder.value?.totalBudget);
const priceVariationOrderAction = useStoreApiAction(variationOrdersStore.priceVariationOrder);

const priceVariationOrder = () => {
  if(sumPrice.value) {
    priceVariationOrderAction.request(variationOrder.value.id, { budget: sumPrice.value }).then(() => {
      cancelEditSumPrice();
    }).catch((error) => {
      console.log(error);
    });
  }
};

const deleteVariationOrderAction = useStoreApiAction(variationOrdersStore.deleteVariationOrder);

const deleteVariationOrder = () => {
  Swal.fire({
    icon: 'warning',
    title: 'Delete Variation Order?',
    text: 'Are you sure you want to delete this variation order? This action cannot be undone.',
    showConfirmButton: true,
    showCancelButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      deleteVariationOrderAction.request(variationOrder.value.id).then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Variation Order Deleted.',
        });
      });
    }
  }).catch((error) => {
    if(error?.status === 403) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to delete variation order.',
        text: error?.data?.message,
      });
    } else if(error?.status === 422) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to delete variation order.',
        text: error?.data?.message,
      });
    }
  });
};

const showContractItem = computed(() => {
  return variationOrderForm.form.package_trade_item_id && variationOrder.value?.package_trade_item_id;
});
</script>

<style scoped></style>
